/**
 * Code-Suche für den Scanner im Verkauf (PB-1) – austauschbar.
 *
 * Schnittstelle: Eine `CodeSuche` bekommt den gescannten bzw. getippten Code (Rohtext, ohne
 * Abschlusszeichen) und liefert ein `CodeSuchergebnis`. Der Verkauf reagiert darauf einheitlich:
 *
 * - `artikel` mit genau einem verkaufbaren Treffer und ohne gesperrte → Position in den Bon
 *   (Menge aus dem Ziffernblock, wie beim Tippen auf die Kachel);
 * - `artikel` mit mehreren Treffern bzw. mit zusätzlichen gesperrten → Auswahldialog, nie
 *   automatisch der erste (gesperrte erscheinen dort mit Grund und sind nicht wählbar);
 * - `gesperrt` → nur gesperrte Artikel: Meldung mit Sperrgrund, nichts in den Bon;
 * - `gutschein` → Einlösedialog mit vorbelegtem Code;
 * - `unbekannt` → verständliche Meldung (`hinweis`), nichts in den Bon.
 *
 * `stammCodeSuche` sucht über den Index `codeTrefferliste` (Barcode, EAN, Artikelnummer exakt,
 * danach GTIN-Normalisierung) – dieselbe Grundlage wie Suchfeld und Handsuche.
 */
import {
  gewichtsPreisHinweis,
  istGewichtsPreisCode,
  kannVerkuerzteGtinSein,
  ohneErsteZiffer,
  protokolliereScan,
  upcEZuUpcA,
  zusatzFormen,
} from './barcode';
import { istGutscheinCode, normalisiereCode } from './gutschein';
import { normalisiereEan } from './lager';
import { codeTrefferliste, type GesperrteKachel, type Schnellwahl } from './schnellwahl';

export type CodeSuchergebnis<T = Schnellwahl> =
  | { art: 'artikel'; code: string; treffer: T[]; gesperrt?: GesperrteKachel[] }
  | { art: 'gesperrt'; code: string; gesperrt: GesperrteKachel[]; hinweis: string }
  | { art: 'gutschein'; code: string }
  | { art: 'unbekannt'; code: string; hinweis: string };

/** Kacheln und gesperrte Artikel einer Konfiguration (Teil von `SchnellwahlKonfig`). */
export interface CodeStamm {
  kacheln: readonly Schnellwahl[];
  gesperrt?: readonly GesperrteKachel[] | undefined;
}

export type CodeSuche<T = Schnellwahl> = (
  code: string,
) => CodeSuchergebnis<T> | Promise<CodeSuchergebnis<T>>;

/**
 * Vergleichsformen eines Codes: der Rohtext und – bei Ziffern – die EAN-13-Form (UPC-A mit
 * 12 Stellen und GTIN-14 mit führender 0 werden wie im Lager zur EAN-13). So findet ein
 * Datalogic-/Honeywell-Scanner, der UPC-A oder GTIN-14 liefert, denselben Artikel.
 */
export function codeVarianten(code: string): string[] {
  const roh = code.trim();
  const varianten = new Set<string>([roh]);
  const ean = normalisiereEan(roh);
  if (ean !== null) {
    varianten.add(ean);
    if (ean.startsWith('0')) {
      varianten.add(ean.slice(1)); // UPC-A-Form eines Stammeintrags mit 12 Stellen
    }
  }
  return [...varianten];
}

/**
 * Vollständiger Artikelcode (EAN-8, UPC-A, EAN-13, GTIN-14)? Dann behandelt der Verkauf eine
 * Eingabe im Suchfeld wie einen Scan (exakte Suche statt Anfangstreffer).
 */
export function istVollerArtikelcode(text: string): boolean {
  return /^(\d{8}|\d{12,14})$/.test(text.trim());
}

/** Warum ein Ziffern-Code nicht passt – für die Meldung an der Kasse. */
function hinweisZuZiffern(code: string): string {
  if (istGewichtsPreisCode(code)) {
    return gewichtsPreisHinweis(code);
  }
  if (
    /^\d{8}$|^\d{12,14}$/.test(code) &&
    normalisiereEan(code) === null &&
    upcEZuUpcA(code) === null
  ) {
    if (/^[1-9]\d{13}$/.test(code)) {
      return `Code ${code} ist eine Umverpackungs-GTIN (Karton) – Einzelartikel scannen oder Artikel anlegen`;
    }
    return `Code ${code} hat eine falsche Prüfziffer – nochmals scannen`;
  }
  return `Kein Artikel mit Code ${code} – Artikel suchen oder im Backoffice anlegen`;
}

/** Je Kachelliste: Stamm-GTINs ohne erste Ziffer (EAN/Barcode, nie Artikelnummer). */
const verkuerztCache = new WeakMap<readonly Schnellwahl[], ReadonlySet<string>>();

function verkuerzteStammCodes(kacheln: readonly Schnellwahl[]): ReadonlySet<string> {
  let formen = verkuerztCache.get(kacheln);
  if (formen === undefined) {
    const neu = new Set<string>();
    for (const k of kacheln) {
      for (const c of [k.ean, k.barcode]) {
        const f = c === undefined ? null : ohneErsteZiffer(c);
        if (f !== null) {
          neu.add(f);
        }
      }
    }
    formen = neu;
    verkuerztCache.set(kacheln, formen);
  }
  return formen;
}

/**
 * Go-Live (A4): Fehlt einem Scan die erste Ziffer, darf er nie einen Artikel treffen – auch
 * nicht exakt über eine zufällig gleiche Artikelnummer bzw. einen internen Code. Erkannt, wenn
 * der Code keine gültige GTIN ist und das Ende einer EAN-8/EAN-13 des Stamms bildet.
 */
function istVerkuerzterStammCode(konfig: CodeStamm, roh: string): boolean {
  if (!kannVerkuerzteGtinSein(roh)) {
    return false;
  }
  return (
    verkuerzteStammCodes(konfig.kacheln).has(roh) ||
    (konfig.gesperrt !== undefined && verkuerzteStammCodes(konfig.gesperrt).has(roh))
  );
}

/** Meldung zu einem Scan, dem vermutlich die erste Ziffer fehlt. */
export function verkuerztHinweis(code: string): string {
  return `Code ${code} unvollständig (erste Ziffer fehlt vermutlich) – nichts übernommen, bitte erneut scannen`;
}

/** Meldung zu gesperrten Treffern (Bezeichnung und Grund je Artikel). */
export function gesperrtHinweis(code: string, gesperrt: readonly GesperrteKachel[]): string {
  const teile = gesperrt.map((g) => `„${g.bezeichnung}“ gesperrt: ${g.grund}`);
  return `Code ${code}: ${teile.join('; ')} – nicht verkaufbar`;
}

/**
 * Code-Suche über Artikelstamm bzw. Schnellwahl (PB-1): alle Treffer aus `codeTrefferliste`,
 * gesperrte mit Grund. Ein Code, der zugleich Gutschein- und Artikelcode ist, löst nichts aus.
 */
export function stammCodeSuche(konfig: CodeStamm): (code: string) => CodeSuchergebnis {
  return (code) => {
    const roh = code.trim();
    if (roh === '') {
      return { art: 'unbekannt', code: roh, hinweis: 'Leerer Code' };
    }
    if (istVerkuerzterStammCode(konfig, roh)) {
      return { art: 'unbekannt', code: roh, hinweis: verkuerztHinweis(roh) };
    }
    const liste = codeTrefferliste(konfig.kacheln, konfig.gesperrt, roh);
    if (liste.length === 0) {
      // UPC-E bzw. mit Nullen aufgefüllte EAN-8: dieselbe Ware in der Form des Stamms suchen
      for (const form of zusatzFormen(roh)) {
        for (const t of codeTrefferliste(konfig.kacheln, konfig.gesperrt, form)) {
          if (!liste.some((x) => x.kachel === t.kachel)) {
            liste.push(t);
          }
        }
      }
    }
    if (istGewichtsPreisCode(roh)) {
      // Waagen-Barcode (Präfix 2x): nur erkennen und protokollieren, nie als kg deuten
      protokolliereScan({
        art: 'gewicht-preis',
        code: roh,
        zeit: performance.now(),
        treffer: liste.length,
      });
    }
    const numerisch = /^\d+$/.test(roh);
    const gs = numerisch ? null : normalisiereCode(roh);
    const gutschein = gs !== null && istGutscheinCode(gs);
    if (liste.length === 0) {
      if (gutschein) {
        return { art: 'gutschein', code: gs };
      }
      return {
        art: 'unbekannt',
        code: roh,
        hinweis: numerisch ? hinweisZuZiffern(roh) : `Unbekannter Code ${roh}`,
      };
    }
    if (gutschein) {
      return {
        art: 'unbekannt',
        code: roh,
        hinweis: `Code ${roh} ist zugleich Gutschein- und Artikelcode – Gutschein über „Gutschein“ einlösen, Artikel über die Suche wählen`,
      };
    }
    const treffer = liste.filter((t) => t.gesperrt === null).map((t) => t.kachel);
    const gesperrt = liste.flatMap((t) => (t.gesperrt === null ? [] : [t.gesperrt]));
    if (treffer.length === 0) {
      return { art: 'gesperrt', code: roh, gesperrt, hinweis: gesperrtHinweis(roh, gesperrt) };
    }
    return gesperrt.length === 0
      ? { art: 'artikel', code: roh, treffer }
      : { art: 'artikel', code: roh, treffer, gesperrt };
  };
}

/** Code-Suche nur über eine Kachelliste (ohne gesperrte Artikel) – für Tests und Altaufrufer. */
export function kachelCodeSuche(
  kacheln: readonly Schnellwahl[],
): (code: string) => CodeSuchergebnis {
  return stammCodeSuche({ kacheln });
}

/**
 * Ist der Text im Suchfeld ein Artikelcode (voller GTIN-Code oder Treffer im Code-Index)? Dann
 * läuft Enter wie ein Scan (Sperren, Auswahl bei Mehrdeutigkeit, Sperrgrund).
 */
export function istArtikelcodeImStamm(konfig: CodeStamm, text: string): boolean {
  const t = text.trim();
  return (
    t !== '' &&
    (istVollerArtikelcode(t) || codeTrefferliste(konfig.kacheln, konfig.gesperrt, t).length > 0)
  );
}
